import { POPTIRA_LOCALES } from '@/common/i18n/messages'

const DEFAULT_LOCALE = 'en-US' as const

export type SupportedLocale = typeof DEFAULT_LOCALE

export function getLocale(): SupportedLocale {
  return DEFAULT_LOCALE
}

export function setLocale(_locale: string): SupportedLocale {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = 'en'
  }
  applyI18n()
  return DEFAULT_LOCALE
}

function messages(): Record<string, string> {
  return POPTIRA_LOCALES[DEFAULT_LOCALE] ?? {}
}

export function t(key: string, _locale?: SupportedLocale): string {
  return messages()[key] ?? key
}

export function applyI18n(_locale?: SupportedLocale): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = 'en'

  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n')
    if (!key) return
    if (el.tagName === 'TITLE') {
      document.title = t(key)
      return
    }
    el.textContent = t(key)
  })

  document.querySelectorAll('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html')
    if (!key) return
    el.innerHTML = t(key)
  })

  document.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder')
    if (!key || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return
    el.placeholder = t(key)
  })
}

export function toggleLocale(): SupportedLocale {
  return setLocale(DEFAULT_LOCALE)
}

export function exposeI18nGlobals(): void {
  window.PoptiraI18n = {
    LOCALE_KEY: 'POPTIRA_UMI_LOCALE',
    DEFAULT_LOCALE,
    SUPPORTED: [DEFAULT_LOCALE],
    getLocale,
    setLocale,
    toggleLocale,
    t,
    applyI18n,
  }
  window.t = t
  applyI18n()
}

declare global {
  interface Window {
    PoptiraI18n: {
      LOCALE_KEY: string
      DEFAULT_LOCALE: string
      SUPPORTED: string[]
      getLocale: () => SupportedLocale
      setLocale: (locale: string) => SupportedLocale
      toggleLocale: () => SupportedLocale
      t: (key: string, locale?: SupportedLocale) => string
      applyI18n: (locale?: SupportedLocale) => void
    }
    t: (key: string, locale?: SupportedLocale) => string
  }
}
